import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

/** Ant Design `Space` replacement — flex row/column with a gap. */
const GAP: Record<string, string> = { small: 'gap-2', middle: 'gap-3', large: 'gap-4' };

/** antd `Space.Compact` — groups adjacent form controls into a connected row/column. */
function Compact({
  direction = 'horizontal', block, className, style, children,
}: {
  direction?: 'horizontal' | 'vertical';
  block?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        block ? 'flex w-full' : 'inline-flex',
        direction === 'vertical' ? 'flex-col gap-px' : 'flex-row gap-1.5',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

function SpaceBase({
  direction = 'horizontal', size = 'small', wrap, align, className, style, children, ...rest
}: {
  direction?: 'horizontal' | 'vertical';
  size?: 'small' | 'middle' | 'large' | number | [number, number];
  wrap?: boolean;
  align?: 'start' | 'end' | 'center' | 'baseline';
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>) {
  const alignCls = align === 'center' ? 'items-center' : align === 'end' ? 'items-end' : align === 'baseline' ? 'items-baseline' : direction === 'horizontal' ? 'items-center' : 'items-stretch';
  const gapStyle = Array.isArray(size)
    ? { columnGap: size[0], rowGap: size[1], ...style }
    : typeof size === 'number' ? { gap: size, ...style } : style;
  return (
    <div
      className={cn('flex', direction === 'vertical' ? 'flex-col' : 'flex-row', wrap && 'flex-wrap', alignCls, typeof size === 'string' && GAP[size], className)}
      style={gapStyle}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Exported as `Space`, with `Space.Compact` (antd-compatible static). */
export const Space = Object.assign(SpaceBase, { Compact });
